import { useState } from 'react';
import { AlertTriangle, Boxes, PackageX, Search } from 'lucide-react';

import type { AdminStore } from '@/client/types/stores';
import type { StockStatus, StoreCatalogListing, StoreInventoryAdjustment, StoreInventoryLine } from '@/client/types/stores/parity';
import { useStoreCatalogListingQuery, useStoreInventoryQuery, useStoreVariantSuggestionsQuery } from '@/client/api/stores/parity.query';
import { useAdjustStoreInventory, useLinkStoreVariants, useUnlinkStoreVariant, useUpdateStoreCatalogDetails } from '@/client/api/stores/parity.mutation';
import { PERMISSIONS } from '@/client/config/permissions';
import { useGate } from '@/client/hooks/useGate';
import { Input } from '@/components/ui/input';
import { cn } from '@/lib/utils';

import { Pager, Pill, SectionCard, label, money, TableSkeleton } from './shared';
import { ReasonDialog } from './ReasonDialog';
import { FieldRow, Toggle } from './settings/controls';

const LIMIT = 25;

const STOCK_TONE: Record<StockStatus, string> = {
  in_stock: 'bg-green-100 text-green-800',
  low_stock: 'bg-amber-100 text-amber-800',
  out_of_stock: 'bg-red-100 text-red-800',
};

const FILTERS: { key: '' | StockStatus; label: string; icon: React.ReactNode }[] = [
  { key: '', label: 'All', icon: <Boxes className="h-3.5 w-3.5" /> },
  { key: 'low_stock', label: 'Low stock', icon: <AlertTriangle className="h-3.5 w-3.5" /> },
  { key: 'out_of_stock', label: 'Out of stock', icon: <PackageX className="h-3.5 w-3.5" /> },
  { key: 'in_stock', label: 'In stock', icon: null },
];

interface AdjustForm {
  delta: string;
  low_stock_threshold: string;
  min_order_qty: string;
  max_order_qty: string;
  backorder_allowed: boolean;
  listing_status: '' | 'active' | 'draft' | 'inactive';
}

const toForm = (l: StoreInventoryLine): AdjustForm => ({
  delta: '',
  low_stock_threshold: String(l.low_stock_threshold),
  min_order_qty: String(l.min_order_qty),
  max_order_qty: l.max_order_qty != null ? String(l.max_order_qty) : '',
  backorder_allowed: l.backorder_allowed,
  listing_status: '',
});

/** Only the fields that changed, in the shape `PUT /inventory/<eiid>` takes. */
const diff = (l: StoreInventoryLine, f: AdjustForm): StoreInventoryAdjustment => {
  const out: StoreInventoryAdjustment = {};
  const delta = Number(f.delta);
  if (f.delta.trim() && Number.isInteger(delta) && delta !== 0) out.delta = delta;
  if (f.low_stock_threshold !== String(l.low_stock_threshold)) out.low_stock_threshold = Number(f.low_stock_threshold);
  if (f.min_order_qty !== String(l.min_order_qty)) out.min_order_qty = Number(f.min_order_qty);
  const currentMax = l.max_order_qty != null ? String(l.max_order_qty) : '';
  if (f.max_order_qty !== currentMax) out.max_order_qty = f.max_order_qty.trim() ? Number(f.max_order_qty) : null;
  if (f.backorder_allowed !== l.backorder_allowed) out.backorder_allowed = f.backorder_allowed;
  if (f.listing_status) out.listing_status = f.listing_status;
  return out;
};

const AdjustDialog: React.FC<{ storeid: number; line: StoreInventoryLine; onClose: () => void }> = ({ storeid, line, onClose }) => {
  const [form, setForm] = useState<AdjustForm>(toForm(line));
  const adjust = useAdjustStoreInventory();
  const changes = diff(line, form);
  const nextQty = line.quantity + (changes.delta ?? 0);
  const belowReserved = nextQty < line.reserved;
  const set = <K extends keyof AdjustForm>(k: K, v: AdjustForm[K]) => setForm((f) => ({ ...f, [k]: v }));

  return (
    <ReasonDialog
      open
      title={`Adjust ${line.entity_name ?? `listing #${line.eid}`}`}
      description={`On hand ${line.quantity}, reserved ${line.reserved}, available ${line.available}. The seller is notified with your reason.`}
      confirmLabel="Save"
      reasonRequired
      loading={adjust.isPending}
      confirmDisabled={Object.keys(changes).length === 0 || belowReserved}
      onConfirm={(reason) =>
        adjust.mutate({ storeid, eiid: line.eiid, reason, ...changes }, { onSuccess: onClose })
      }
      onClose={onClose}
    >
      <div className="divide-y divide-gray-100 rounded-lg border border-gray-100 px-3">
        <FieldRow label="Stock change" hint={`New on-hand: ${nextQty}${belowReserved ? ' — below reserved units' : ''}`}>
          <Input type="number" value={form.delta} onChange={(e) => set('delta', e.target.value)} className="w-24" placeholder="+5 / -2" />
        </FieldRow>
        <FieldRow label="Low-stock alert at">
          <Input type="number" min={0} value={form.low_stock_threshold} onChange={(e) => set('low_stock_threshold', e.target.value)} className="w-24" />
        </FieldRow>
        <FieldRow label="Order quantity" hint="Minimum and maximum units per order.">
          <div className="flex gap-1">
            <Input type="number" min={1} value={form.min_order_qty} onChange={(e) => set('min_order_qty', e.target.value)} className="w-16" />
            <Input type="number" min={1} value={form.max_order_qty} onChange={(e) => set('max_order_qty', e.target.value)} className="w-16" placeholder="∞" />
          </div>
        </FieldRow>
        <FieldRow label="Allow backorders">
          <Toggle checked={form.backorder_allowed} onChange={(v) => set('backorder_allowed', v)} />
        </FieldRow>
        <FieldRow label="Listing status" hint={`Now: ${label(line.listing_status?.toLowerCase())}`}>
          <select
            value={form.listing_status}
            onChange={(e) => set('listing_status', e.target.value as AdjustForm['listing_status'])}
            className="rounded-md border border-gray-300 bg-white px-2 py-1.5 text-sm"
          >
            <option value="">No change</option>
            <option value="active">Active (live)</option>
            <option value="draft">Draft</option>
            <option value="inactive">Inactive</option>
          </select>
        </FieldRow>
      </div>
    </ReasonDialog>
  );
};

/** Values of eatid 131 and 14, as the API validates them. */
const DELIVERY_OPTIONS = ['Standard (Paid)', 'Standard (Free)', 'Pickup'];
const CONDITIONS = [
  'New', 'Used - Like New', 'Used - Excellent', 'Used - Very Good', 'Used - Good', 'Used - Fair', 'Used - Poor',
  'Refurbished', 'For Parts or Not Working',
];

/**
 * A catalogue listing's essentials (`GET /listing/<eid>/siblings`, `PUT /listing/<eid>/details`,
 * ff-api#1497): the seller's offer, not the catalogue's product. Same rules as Store Manager.
 */
const CatalogDetailsDialog: React.FC<{ storeid: number; line: StoreInventoryLine; onClose: () => void }> = ({ storeid, line, onClose }) => {
  const { data: listing, isLoading } = useStoreCatalogListingQuery(storeid, line.eid);
  return isLoading || !listing ? null : (
    <CatalogDetailsForm storeid={storeid} line={line} listing={listing} onClose={onClose} />
  );
};

const CatalogDetailsForm: React.FC<{ storeid: number; line: StoreInventoryLine; listing: StoreCatalogListing; onClose: () => void }> = ({ storeid, line, listing, onClose }) => {
  const initial = Object.fromEntries(Object.entries(listing.details).map(([k, v]) => [k, v ?? '']));
  const [values, setValues] = useState<Record<string, string>>(initial);
  const update = useUpdateStoreCatalogDetails();
  const changes = Object.fromEntries(Object.entries(values).filter(([k, v]) => v !== initial[k]));
  const set = (k: string) => (v: string) => setValues((f) => ({ ...f, [k]: v }));
  const select = (k: string, options: string[], empty: string) => (
    <select value={values[k] ?? ''} onChange={(e) => set(k)(e.target.value)} className="rounded-md border border-gray-300 bg-white px-2 py-1.5 text-sm">
      <option value="">{empty}</option>
      {options.map((o) => <option key={o} value={o}>{o}</option>)}
    </select>
  );
  const text = (k: string, className = 'w-40') => (
    <Input value={values[k] ?? ''} onChange={(e) => set(k)(e.target.value)} className={className} />
  );

  return (
    <ReasonDialog
      open
      title={`Details of ${line.entity_name ?? `listing #${line.eid}`}`}
      description="From the catalogue: name, category and specs are not editable. A new colour or storage size moves the listing onto that catalogue product when there is one. The seller is notified with your reason."
      confirmLabel="Save"
      reasonRequired
      loading={update.isPending}
      confirmDisabled={Object.keys(changes).length === 0}
      onConfirm={(reason) => update.mutate({ storeid, eid: line.eid, reason, ...changes }, { onSuccess: onClose })}
      onClose={onClose}
    >
      <div className="divide-y divide-gray-100 rounded-lg border border-gray-100 px-3">
        <FieldRow label="Condition">{select('condition', CONDITIONS, 'Not set')}</FieldRow>
        <FieldRow label="Colour">{text('color_string')}</FieldRow>
        <FieldRow label="Storage">{text(listing.storage_attribute, 'w-28')}</FieldRow>
        <FieldRow label="Delivery">{select('item_delivery', DELIVERY_OPTIONS, 'Store default')}</FieldRow>
        <FieldRow label="Delivery details">{text('item_delivery_details', 'w-56')}</FieldRow>
        <FieldRow label="Warranty">{select('warranty_support', ['Yes'], 'No warranty')}</FieldRow>
        <FieldRow label="Warranty details">{text('warranty_support_details', 'w-56')}</FieldRow>
        <FieldRow label="SKU">{text('sku')}</FieldRow>
        <FieldRow label="Barcode (GTIN)">{text('gtin')}</FieldRow>
      </div>
    </ReasonDialog>
  );
};

type VariantAction = { kind: 'link'; eids: number[] } | { kind: 'unlink'; line: StoreInventoryLine };

/** Link (`POST /variants`) or unlink (`DELETE /variants/<eid>`) variant listings, as the seller can. */
const VariantDialog: React.FC<{ storeid: number; action: VariantAction; onDone: () => void; onClose: () => void }> = ({ storeid, action, onDone, onClose }) => {
  const link = useLinkStoreVariants();
  const unlink = useUnlinkStoreVariant();
  const isLink = action.kind === 'link';

  return (
    <ReasonDialog
      open
      title={isLink ? `Link ${action.eids.length} listings as variants` : `Unlink ${action.line.entity_name ?? `listing #${action.line.eid}`}`}
      description={
        isLink
          ? 'Shoppers on one of these listings can switch to the others. Listings in different categories, or naming a different make or model, are refused. The seller is notified with your reason.'
          : 'This listing stops appearing as an option on its variants. The seller is notified with your reason.'
      }
      confirmLabel={isLink ? 'Link' : 'Unlink'}
      reasonRequired
      loading={link.isPending || unlink.isPending}
      onConfirm={(reason) =>
        isLink
          ? link.mutate({ storeid, reason, eids: action.eids }, { onSuccess: onDone })
          : unlink.mutate({ storeid, reason, eid: action.line.eid }, { onSuccess: onDone })
      }
      onClose={onClose}
    />
  );
};

/**
 * Stock table (`GET /<id>/inventory`) with the seller's adjust action
 * (`PUT /inventory/<eiid>`, `admin.store.inventory`). Stock can't drop below
 * units reserved for open orders. Rows can also be linked as variants of one
 * product (`POST /variants`) and unlinked (`DELETE /variants/<eid>`). Catalogue listings
 * also get their essentials (`PUT /listing/<eid>/details`).
 */
export const InventoryTab: React.FC<{ store: AdminStore }> = ({ store }) => {
  const [filter, setFilter] = useState<'' | StockStatus>('');
  const [search, setSearch] = useState('');
  const [offset, setOffset] = useState(0);
  const [editing, setEditing] = useState<StoreInventoryLine | null>(null);
  const [detailing, setDetailing] = useState<StoreInventoryLine | null>(null);
  const [selected, setSelected] = useState<number[]>([]);
  const [variantAction, setVariantAction] = useState<VariantAction | null>(null);
  const canEdit = useGate(PERMISSIONS.store.inventory).allowed;

  const { data, isLoading, isFetching } = useStoreInventoryQuery(store.storeid, {
    stock_status: filter || undefined,
    search: search.trim() || undefined,
    limit: LIMIT,
    offset,
  });
  const lines = data?.inventory ?? [];
  const suggestions = useStoreVariantSuggestionsQuery(store.storeid).data?.suggestions ?? [];
  const total = data?.total_count ?? 0;

  return (
    <SectionCard
      bare
      description="Tracked stock for this store's listings."
      actions={<span className="text-xs text-gray-400">{isFetching ? 'Refreshing…' : `${total} lines`}</span>}
    >
      <div className="mb-3 flex flex-wrap items-center gap-2">
        <div className="inline-flex rounded-lg border border-gray-200 p-0.5 text-xs">
          {FILTERS.map((f) => (
            <button
              key={f.key || 'all'}
              type="button"
              onClick={() => { setFilter(f.key); setOffset(0); }}
              className={cn('flex items-center gap-1 rounded-md px-2.5 py-1 font-medium', filter === f.key ? 'bg-teal-600 text-white' : 'text-gray-600 hover:bg-gray-100')}
            >
              {f.icon}
              {f.label}
            </button>
          ))}
        </div>
        <div className="relative min-w-[200px] flex-1">
          <Search className="pointer-events-none absolute left-2.5 top-2.5 h-4 w-4 text-gray-400" />
          <Input value={search} onChange={(e) => { setSearch(e.target.value); setOffset(0); }} placeholder="Name, SKU or FFID" className="pl-8" />
        </div>
      </div>

      {suggestions.length > 0 && (
        <details className="mb-3 rounded-lg border border-teal-100 bg-teal-50/60 text-sm">
          <summary className="cursor-pointer px-3 py-2 font-medium text-gray-900">
            {suggestions.length} {suggestions.length === 1 ? 'product looks' : 'products look'} like they come in several options
            <span className="font-normal text-gray-600"> — the same list the seller sees</span>
          </summary>
          <ul className="divide-y divide-teal-100 border-t border-teal-100">
            {suggestions.map((s) => (
              <li key={s.key} className="flex flex-wrap items-center gap-x-3 gap-y-1 px-3 py-2">
                <span className="font-medium text-gray-900">{[s.make, s.model_name].filter(Boolean).join(' ')}</span>
                <span className="min-w-0 flex-1 text-gray-600">
                  {s.listings.map((l) => [l.colour, l.storage].filter(Boolean).join(' / ') || l.name || `#${l.eid}`).join(', ')}
                </span>
                <button
                  type="button"
                  disabled={!canEdit}
                  onClick={() => setVariantAction({ kind: 'link', eids: s.listings.map((l) => l.eid) })}
                  className="rounded-md bg-teal-600 px-2.5 py-1 text-xs font-medium text-white hover:bg-teal-700 disabled:cursor-not-allowed disabled:opacity-40"
                >
                  Link {s.listings.length}
                </button>
              </li>
            ))}
          </ul>
        </details>
      )}

      {selected.length > 0 && (
        <div className="mb-3 flex flex-wrap items-center gap-3 rounded-lg bg-teal-50 px-3 py-2 text-sm">
          <span className="text-gray-700">{selected.length} selected{selected.length < 2 ? ' — pick at least one more' : ''}</span>
          <button
            type="button"
            disabled={selected.length < 2}
            onClick={() => setVariantAction({ kind: 'link', eids: selected })}
            className="rounded-md bg-teal-600 px-2.5 py-1 text-xs font-medium text-white hover:bg-teal-700 disabled:cursor-not-allowed disabled:opacity-40"
          >
            Link as variants
          </button>
          <button type="button" onClick={() => setSelected([])} className="text-xs text-gray-600 hover:text-gray-900">Clear</button>
        </div>
      )}

      {isLoading ? (
        <TableSkeleton cols={6} />
      ) : lines.length === 0 ? (
        <p className="py-8 text-center text-sm text-gray-400">No inventory lines match.</p>
      ) : (
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200 text-sm">
            <thead>
              <tr className="text-left text-xs uppercase text-gray-400">
                <th className="py-2 pr-2"><span className="sr-only">Select</span></th>
                <th className="py-2 pr-4">Listing</th>
                <th className="py-2 pr-4">SKU</th>
                <th className="py-2 pr-4 text-right">On hand</th>
                <th className="py-2 pr-4 text-right">Reserved</th>
                <th className="py-2 pr-4 text-right">Available</th>
                <th className="py-2 pr-4">Price</th>
                <th className="py-2 pr-4">Stock</th>
                <th className="py-2 pr-4">Listing</th>
                <th className="py-2" />
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {lines.map((l) => (
                <tr key={l.eiid}>
                  <td className="py-2 pr-2">
                    <input
                      type="checkbox"
                      aria-label={`Select ${l.entity_name ?? `listing #${l.eid}`}`}
                      disabled={!canEdit}
                      checked={selected.includes(l.eid)}
                      onChange={() => setSelected((prev) => (prev.includes(l.eid) ? prev.filter((e) => e !== l.eid) : [...prev, l.eid]))}
                    />
                  </td>
                  <td className="max-w-[220px] py-2 pr-4 font-medium text-gray-900">
                    <div className="truncate" title={l.entity_name ?? undefined}>{l.entity_name ?? `Listing #${l.eid}`}</div>
                    {l.variant_group && <div className="mt-1"><Pill tone="bg-blue-100 text-blue-800">Variant</Pill></div>}
                  </td>
                  <td className="py-2 pr-4 font-mono text-xs text-gray-500">{l.sku ?? '—'}</td>
                  <td className="py-2 pr-4 text-right tabular-nums">{l.quantity}</td>
                  <td className="py-2 pr-4 text-right tabular-nums text-gray-500">{l.reserved}</td>
                  <td className="py-2 pr-4 text-right tabular-nums font-medium">{l.available}</td>
                  <td className="py-2 pr-4 tabular-nums">{l.price ? money(l.price.price) : '—'}</td>
                  <td className="py-2 pr-4"><Pill tone={STOCK_TONE[l.stock_status]}>{label(l.stock_status)}</Pill></td>
                  <td className="py-2 pr-4 text-xs capitalize text-gray-600">{label(l.listing_status?.toLowerCase())}</td>
                  <td className="whitespace-nowrap py-2 text-right">
                    {l.variant_group && (
                      <button
                        type="button"
                        disabled={!canEdit}
                        onClick={() => setVariantAction({ kind: 'unlink', line: l })}
                        className="mr-1 rounded-md border border-gray-200 px-2 py-1 text-xs font-medium text-gray-700 hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-40"
                      >
                        Unlink
                      </button>
                    )}
                    {l.is_catalog && (
                      <button
                        type="button"
                        disabled={!canEdit}
                        onClick={() => setDetailing(l)}
                        className="mr-1 rounded-md border border-gray-200 px-2 py-1 text-xs font-medium text-gray-700 hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-40"
                      >
                        Details
                      </button>
                    )}
                    <button
                      type="button"
                      disabled={!canEdit}
                      onClick={() => setEditing(l)}
                      className="rounded-md border border-gray-200 px-2 py-1 text-xs font-medium text-gray-700 hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-40"
                    >
                      Adjust
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <Pager total={total} limit={LIMIT} offset={offset} onChange={setOffset} />
      {editing && <AdjustDialog storeid={store.storeid} line={editing} onClose={() => setEditing(null)} />}
      {detailing && <CatalogDetailsDialog storeid={store.storeid} line={detailing} onClose={() => setDetailing(null)} />}
      {variantAction && (
        <VariantDialog
          storeid={store.storeid}
          action={variantAction}
          onDone={() => { setVariantAction(null); setSelected([]); }}
          onClose={() => setVariantAction(null)}
        />
      )}
    </SectionCard>
  );
};
